import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Descriptions, Modal, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { listSorties, listSortiesCount, approveSortie, refuseSortie, scanIn, scanOut, exportSortiesCsv } from '../../api/sorties';
import { generateSupervisorSortieQR, generateSupervisorReturnQR, getQrStatus } from '../../api/qr';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard, { AntPagination } from '../../components/TableCard';
import { SelectWrap, DateInput, SearchBar } from '../../components/ToolbarControls';
import { StatusTag, StudentStatusTag, CheckSuccessIcon, ViewIcon, QrIcon, CheckInIcon, CheckOutIcon, ApproveIcon, RefuseIcon } from '../../components/UIKit';
import { isPageVisible } from '../../utils/pageVisibility';

export default function Sorties() {
  const { message, notification } = App.useApp();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [tableSearch, setTableSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    const overdueParam = sp.get('overdue');
    const statutParam = sp.get('statut');
    const hasAny = sp.has('overdue') || sp.has('statut') || sp.has('from') || sp.has('to');
    return {
      statut: statutParam || '',
      overdue: overdueParam === 'true' || (!hasAny ? true : false),
      from: sp.get('from') || null,
      to: sp.get('to') || null,
    };
  });

  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [qrBySortieId, setQrBySortieId] = useState({});
  const [qrReturnBySortieId, setQrReturnBySortieId] = useState({});
  const [viewing, setViewing] = useState(null);

  const filteredItems = useMemo(() => {
    const q = String(tableSearch || '').trim().toLowerCase();
    const base = Array.isArray(items) ? items : [];
    if (!q) return base;
    return base.filter((r) => {
      const code = String(r?.Student?.student_code || '').toLowerCase();
      const prenom = String(r?.Student?.User?.prenom || '').toLowerCase();
      const nom = String(r?.Student?.User?.nom || '').toLowerCase();
      const email = String(r?.Student?.User?.email || '').toLowerCase();
      const residence = String(r?.Student?.Residence?.nom || '').toLowerCase();
      const chambre = String(r?.Student?.RoomAssignments?.[0]?.Room?.code || r?.Student?.chambre || '').toLowerCase();
      return code.includes(q) || prenom.includes(q) || nom.includes(q) || email.includes(q) || residence.includes(q) || chambre.includes(q);
    });
  }, [items, tableSearch]);

  const fetchData = useCallback(async (p, f, opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = { page: p, pageSize, sortBy: 'id', sortDir: 'DESC' };
      if (f.statut) params.statut = f.statut;
      if (f.overdue) params.overdue = true;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      const { items: fetched, total: t } = await listSorties(params);
      const sortedItems = [...(fetched || [])].sort((a, b) => {
        const ar = String(a?.statut || '').toUpperCase() === 'PENDING' ? 0 : 1;
        const br = String(b?.statut || '').toUpperCase() === 'PENDING' ? 0 : 1;
        if (ar !== br) return ar - br;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
      setItems(sortedItems);
      setTotal(t);
      setPage(p);
    } catch (e) {
      if (!silent) {
        notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les sorties.'), placement: 'topRight' });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [notification, pageSize]);

  const fetchOverdueCount = useCallback(async () => {
    try { const { total: t } = await listSortiesCount({ overdue: true }); setOverdueCount(Number(t || 0)); } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(1, filters); fetchOverdueCount(); }, [fetchData, fetchOverdueCount, filters]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const hasAny = sp.has('overdue') || sp.has('statut') || sp.has('from') || sp.has('to');
    setFilters((prev) => ({ ...prev, statut: sp.get('statut') || '', overdue: sp.get('overdue') === 'true' || (!hasAny ? true : false), from: sp.get('from') || null, to: sp.get('to') || null }));
  }, [location.search]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!isPageVisible()) return;
      fetchData(page, filters, { silent: true });
      fetchOverdueCount();
    }, 10000);
    const onVis = () => {
      if (isPageVisible()) {
        fetchData(page, filters, { silent: true });
        fetchOverdueCount();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchData, fetchOverdueCount, filters, page]);

  useEffect(() => {
    const onRefresh = () => {
      fetchData(page, filters);
      fetchOverdueCount();
    };
    window.addEventListener('sorties:refresh', onRefresh);
    return () => window.removeEventListener('sorties:refresh', onRefresh);
  }, [fetchData, fetchOverdueCount, filters, page]);

  // QR timer countdown
  useEffect(() => {
    let timer;
    if (qrOpen && qrData?.expires_at) {
      const tick = () => { const ms = new Date(qrData.expires_at).getTime() - Date.now(); setTimeLeft(ms > 0 ? Math.floor(ms / 1000) : 0); };
      tick(); timer = setInterval(tick, 1000);
    } else { setTimeLeft(null); }
    return () => { if (timer) clearInterval(timer); };
  }, [qrOpen, qrData]);

  // Auto-close QR when consumed
  useEffect(() => {
    let id; let stopped = false;
    const token = qrData?.token; const sortieId = qrData?.sortie_id;
    if (!qrOpen || !token) return undefined;
    const poll = async () => {
      try {
        const st = await getQrStatus(token);
        if (st?.used_at) {
          if (stopped) return;
          notification.success({ message: st?.type === 'RETOUR' ? `QR retour consommé (sortie #${sortieId})` : `QR sortie consommé (sortie #${sortieId})`, placement: 'topRight' });
          setQrOpen(false); setQrData(null); fetchData(page, filters); fetchOverdueCount();
        }
        if (st?.expired) {
          if (stopped) return;
          notification.warning({ message: 'QR expiré. Régénérez un nouveau QR.', placement: 'topRight' });
          setQrOpen(false); setQrData(null);
        }
      } catch { /* ignore */ }
    };
    poll(); id = setInterval(poll, 1500);
    return () => { stopped = true; if (id) clearInterval(id); };
  }, [fetchData, fetchOverdueCount, filters, notification, page, qrData, qrOpen]);

  const onApprove = useCallback(async (id) => {
    try {
      await approveSortie(id); message.success('Approuvée'); fetchData(page, filters);
      try { const qr = await generateSupervisorSortieQR(id); setQrBySortieId((p) => ({ ...p, [id]: qr })); setQrData(qr); setQrOpen(true); }
      catch (e) { notification.error({ message: getApiErrorMessage(e, 'QR non généré.'), placement: 'topRight' }); }
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Approbation échouée.'), placement: 'topRight' }); }
  }, [fetchData, filters, message, notification, page]);

  const onRefuse = useCallback(async (id) => {
    try { await refuseSortie(id); message.success('Refusée'); fetchData(page, filters); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Refus échoué.'), placement: 'topRight' }); }
  }, [fetchData, filters, message, notification, page]);

  const onScanOut = useCallback(async (id) => {
    try { await scanOut(id); message.success('Sortie enregistrée'); fetchData(page, filters); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Enregistrement de sortie échoué.'), placement: 'topRight' }); }
  }, [fetchData, filters, message, notification, page]);

  const onScanIn = useCallback(async (id) => {
    try { await scanIn(id); message.success('Retour enregistré'); fetchData(page, filters); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Enregistrement du retour échoué.'), placement: 'topRight' }); }
  }, [fetchData, filters, message, notification, page]);

  const onShowQr = useCallback(async (row) => {
    const existing = qrBySortieId[row.id];
    if (existing?.expires_at && new Date(existing.expires_at).getTime() > Date.now()) { setQrData(existing); setQrOpen(true); return; }
    try { const qr = await generateSupervisorSortieQR(row.id); setQrBySortieId((p) => ({ ...p, [row.id]: qr })); setQrData(qr); setQrOpen(true); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'QR non généré.'), placement: 'topRight' }); }
  }, [notification, qrBySortieId]);

  const onShowReturnQr = useCallback(async (row) => {
    const existing = qrReturnBySortieId[row.id];
    if (existing?.expires_at && new Date(existing.expires_at).getTime() > Date.now()) { setQrData(existing); setQrOpen(true); return; }
    try { const qr = await generateSupervisorReturnQR(row.id); setQrReturnBySortieId((p) => ({ ...p, [row.id]: qr })); setQrData(qr); setQrOpen(true); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'QR retour non généré.'), placement: 'topRight' }); }
  }, [notification, qrReturnBySortieId]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.statut) params.statut = filters.statut;
      if (filters.overdue) params.overdue = true;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const blob = await exportSortiesCsv(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `sorties-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { notification.error({ message: 'Export échoué', placement: 'topRight' }); }
  };

  const [localFrom, setLocalFrom] = useState(filters.from || '');
  const [localTo, setLocalTo] = useState(filters.to || '');

  const applyFilters = () =>
    setFilters((f) => ({ ...f, from: localFrom || null, to: localTo || null }));

  const renderActions = (r) => {
    const isPending = r.statut === 'PENDING';
    const isAccepted = r.statut === 'ACCEPTED';
    const hasSortie = !!r.date_sortie;
    const hasRetour = !!r.date_retour_effective;
    return (
      <div className="actions-cell">
        <button className="action-btn" title="Voir" onClick={() => setViewing(r)}><ViewIcon /></button>
        {isPending && !hasSortie && !hasRetour && (<>
          <button className="action-btn" title="Approuver" onClick={() => onApprove(r.id)}><ApproveIcon /></button>
          <Popconfirm title="Refuser cette sortie ?" okText="Refuser" cancelText="Annuler" okType="danger" onConfirm={() => onRefuse(r.id)}>
            <button className="action-btn danger" title="Refuser"><RefuseIcon /></button>
          </Popconfirm>
        </>)}
        {isAccepted && !hasSortie && !hasRetour && (<>
          <button className="action-btn" title="QR sortie" onClick={() => onShowQr(r)}><QrIcon /></button>
          <button className="action-btn" title="Enregistrer sortie" onClick={() => onScanOut(r.id)}><CheckOutIcon /></button>
        </>)}
        {(isAccepted || isPending) && hasSortie && !hasRetour && (<>
          <button className="action-btn" title="QR retour" onClick={() => onShowReturnQr(r)}><QrIcon /></button>
          <button className="action-btn" title="Enregistrer retour" onClick={() => onScanIn(r.id)}><CheckInIcon /></button>
        </>)}
      </div>
    );
  };

  return (
    <div className="page">
      <TableCard
        label="Sorties"
        loading={loading}
        empty={!loading && filteredItems.length === 0}
        toolbar={<>
          <SelectWrap
            value={filters.statut}
            onChange={(v) => setFilters((f) => ({ ...f, statut: v }))}
            options={[{ value: '', label: 'Tous statuts' }, { value: 'PENDING', label: 'PENDING' }, { value: 'ACCEPTED', label: 'ACCEPTED' }, { value: 'REFUSED', label: 'REFUSED' }, { value: 'RETURNED', label: 'RETURNED' }]}
          />
          <SelectWrap
            value={filters.overdue ? 'overdue' : 'all'}
            onChange={(v) => setFilters((f) => ({ ...f, overdue: v === 'overdue' }))}
            options={[{ value: 'all', label: 'Tous' }, { value: 'overdue', label: 'En retard' }]}
            badge={overdueCount}
          />
          <DateInput value={localFrom} onChange={setLocalFrom} title="Du" />
          <DateInput value={localTo} onChange={setLocalTo} title="Au" />
          <button className="btn-primary" onClick={applyFilters}>Filtrer</button>
          <button className="btn-outline" onClick={exportCsv}>Export Excel</button>
        </>}
        search={<SearchBar value={tableSearch} onChange={setTableSearch} placeholder="Rechercher un étudiant (ID, nom, email, résidence, chambre)" />}
        pagination={<AntPagination current={page} total={total} pageSize={pageSize} onChange={(p) => fetchData(p, filters)} />}
      >
        <table>
          <thead>
            <tr>
              <th>ID étudiant</th>
              <th>Étudiant</th>
              <th>Email</th>
              <th>Résidence</th>
              <th>Chambre</th>
              <th>Destination</th>
              <th>Prévue sortie</th>
              <th>Prévue retour</th>
              <th>Retour effectif</th>
              {filters.overdue && <th>Durée dehors</th>}
              <th>Statut</th>
              <th>Statut étudiant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((r) => {
              const isLatest = !!r.is_latest_for_student;
              const studentStatut = r.Student?.statut;
              let dureesDehors = '';
              if (filters.overdue && r.date_sortie) {
                const start = new Date(r.date_sortie).getTime();
                const diffMs = Date.now() - start;
                if (diffMs > 0) {
                  const hours = Math.floor(diffMs / 3600000);
                  const days = Math.floor(hours / 24);
                  dureesDehors = days > 0 ? `${days}j ${hours % 24}h` : `${hours}h`;
                }
              }
              return (
                <tr key={r.id} className={r.is_overdue ? 'row-late' : ''}>
                  <td>{r.Student?.student_code || '-'}</td>
                  <td>{`${r.Student?.User?.prenom || ''} ${r.Student?.User?.nom || ''}`.trim() || '-'}</td>
                  <td>{r.Student?.User?.email || '-'}</td>
                  <td>{r.Student?.Residence?.nom || '-'}</td>
                  <td>{r.Student?.RoomAssignments?.[0]?.Room?.code || r.Student?.chambre || '-'}</td>
                  <td>{r.destination || '-'}</td>
                  <td>{r.date_sortie_prevue ? dayjs(r.date_sortie_prevue).format('YYYY-MM-DD HH:mm') : '-'}</td>
                  <td>{r.date_retour_prevue ? dayjs(r.date_retour_prevue).format('YYYY-MM-DD HH:mm') : '-'}</td>
                  <td>{r.date_retour_effective ? dayjs(r.date_retour_effective).format('YYYY-MM-DD HH:mm') : '—'}</td>
                  {filters.overdue && <td>{dureesDehors || '-'}</td>}
                  <td>
                    <StatusTag status={r.statut} />
                    {r.is_overdue && <span className="late-dot">En retard</span>}
                  </td>
                  <td>
                    {!isLatest ? <CheckSuccessIcon /> : studentStatut ? <StudentStatusTag status={studentStatut} /> : '-'}
                  </td>
                  <td>{renderActions(r)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>

      {/* QR Modal */}
      <Modal title="QR pour validation" open={qrOpen} onCancel={() => { setQrOpen(false); setQrData(null); }} footer={<button className="btn-outline" onClick={() => { setQrOpen(false); setQrData(null); }}>Fermer</button>}>
        {qrData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 12, color: '#666' }}>Demandez à l'étudiant de scanner ce code pour enregistrer sa sortie.</p>
            {typeof timeLeft === 'number' && <p style={{ fontSize: 12, fontWeight: 600 }}>Expire dans {Math.max(0, timeLeft)}s</p>}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(JSON.stringify({ tok: qrData.token, sig: qrData.sig, sid: qrData.sortie_id, typ: qrData.type, exp: qrData.expires_at }))}`} alt="QR" width={220} height={220} style={{ borderRadius: 8, border: '0.5px solid rgba(0,0,0,0.1)' }} />
            </div>
            <Descriptions bordered size="small" column={1} items={[
              { key: 'token', label: 'Token', children: <code style={{ userSelect: 'all', fontSize: 10 }}>{qrData.token}</code> },
              { key: 'type', label: 'Type', children: qrData.type },
              { key: 'expire', label: 'Expire', children: dayjs(qrData.expires_at).format('YYYY-MM-DD HH:mm:ss') },
            ]} />
          </div>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal title={viewing ? `Sortie #${viewing.id}` : 'Détail'} open={!!viewing} onCancel={() => setViewing(null)} width={800} centered destroyOnClose footer={<button className="btn-outline" onClick={() => setViewing(null)}>Fermer</button>}>
        {viewing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="ID étudiant">{viewing.Student?.student_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="Étudiant">{`${viewing.Student?.User?.prenom || ''} ${viewing.Student?.User?.nom || ''}`.trim() || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email">{viewing.Student?.User?.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Résidence">{viewing.Student?.Residence?.nom || '-'}</Descriptions.Item>
              <Descriptions.Item label="Chambre">{viewing.Student?.RoomAssignments?.[0]?.Room?.code || viewing.Student?.chambre || '-'}</Descriptions.Item>
              <Descriptions.Item label="Statut"><StatusTag status={viewing.statut} /></Descriptions.Item>
            </Descriptions>
            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Destination">{viewing.destination || '-'}</Descriptions.Item>
              <Descriptions.Item label="Motif">{viewing.motif || '-'}</Descriptions.Item>
              <Descriptions.Item label="Sortie prévue">{viewing.date_sortie_prevue ? dayjs(viewing.date_sortie_prevue).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Retour prévu">{viewing.date_retour_prevue ? dayjs(viewing.date_retour_prevue).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Sortie effective">{viewing.date_sortie ? dayjs(viewing.date_sortie).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Retour effectif">{viewing.date_retour_effective ? dayjs(viewing.date_retour_effective).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
}
